// Promos (wave 3, W2_Portal_Tables look): every promo code in the modern
// table, newest first, "Add promo" at the top, Edit in each row and Delete
// in the row menu.
//
// Data: fetchPromos() once when the page opens, as before; nothing else is
// read. Writes are the same calls with the plain promo (no material-table
// tableData any more):
// - "Show in list" switch (admin, critical edits on):
//   editPromo({ ...promo, promo_show: !promo_show }, 'Update').
// - Delete (everyone who can open the page, critical edits on):
//   editPromo(promo, 'Delete').
// The store keeps the promos it loaded (EDIT_PROMOS does not change it), so
// this page shows its own writes until the next visit loads them again.
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Switch from '@mui/material/Switch';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import { api } from 'common';
import { getLangKey } from 'common/src/other/getLangKey';
import { showToast, confirmDialog } from '../components/feedback';
import { DataTable, PageHeader, YesNo, calmCaps, formatMoney, formatNumber, toMs, usePageState } from '../components/modern';

export default function Promos() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { editPromo, fetchPromos } = api;
  const settings = useSelector((s) => s.settingsdata.settings);
  const promodata = useSelector((s) => s.promodata);
  const auth = useSelector((s) => s.auth);
  const isAdmin = !!(auth.profile && auth.profile.usertype === 'admin');
  const critical = !!settings.AllowCriticalEditsAdmin;

  // Promos are fetched on mount (no longer pre-fetched at login).
  useEffect(() => {
    dispatch(fetchPromos());
  }, [dispatch, fetchPromos]);

  // The page and the table's search are kept while staff open a record and
  // come back (owner, 28 Oct); components/modern/pageState.js.
  const [page, setPage] = usePageState('promos', 'page', Number.isInteger(state?.pageNo) && state.pageNo >= 0 ? state.pageNo : 0);
  useEffect(() => {
    if (Number.isInteger(state?.pageNo) && state.pageNo >= 0) setPage(state.pageNo);
  }, [state]);

  // This page's own writes over the loaded list: { source, show: { id: bool }, removed: [id] }.
  const source = promodata.promos;
  const [local, setLocal] = useState({ source, show: {}, removed: [] });
  const mine = useMemo(() => (local.source === source ? local : { source, show: {}, removed: [] }), [local, source]);

  // Newest first, as before (by createdAt).
  const rows = useMemo(() => {
    const list = Array.isArray(source) ? source : [];
    return list
      .filter((p) => p && !mine.removed.includes(p.id))
      .map((p) => (Object.prototype.hasOwnProperty.call(mine.show, p.id) ? { ...p, promo_show: mine.show[p.id] } : p))
      .sort((a, b) => (toMs(b.createdAt) || 0) - (toMs(a.createdAt) || 0));
  }, [source, mine]);

  const shown = (value) => (value ? t(getLangKey(value), { defaultValue: value }) : '');
  const navState = { state: { pageNo: page } };
  const goAdd = () => navigate('/promos/editpromo', navState);
  const goEdit = (row) => navigate(`/promos/editpromo/${row.id}`, navState);

  const toggleShow = (row) => {
    if (!isAdmin) return;
    if (!critical) {
      showToast.warning(t('demo_mode'));
      return;
    }
    const next = !row.promo_show;
    dispatch(editPromo({ ...row, promo_show: next }, 'Update'));
    setLocal({ ...mine, show: { ...mine.show, [row.id]: next } });
  };

  const handleDelete = async (row) => {
    if (!critical) {
      showToast.warning(t('demo_mode'));
      return;
    }
    const ok = await confirmDialog({
      title: t('promos_delete_title', { code: row.promo_code || shown(row.promo_name), defaultValue: 'Delete the promo {{code}}?' }),
      body: t('promos_delete_body', { defaultValue: 'Customers can no longer use it. This cannot be undone.' }),
      confirmLabel: t('delete', { defaultValue: 'Delete' }),
      cancelLabel: t('confirm_cancel', { defaultValue: 'Cancel' }),
      destructive: true,
    });
    if (!ok) return;
    dispatch(editPromo(row, 'Delete'));
    setLocal({ ...mine, removed: mine.removed.concat([row.id]) });
    showToast.success(t('promos_deleted', { defaultValue: 'Promo deleted' }));
  };

  const discountText = (row) => {
    const v = row.promo_discount_value;
    if (v === null || v === undefined || v === '') return '';
    return row.promo_discount_type === 'percentage' ? `${formatNumber(v)}%` : formatMoney(v, settings);
  };

  const columns = [
    {
      title: t('promo_name'),
      field: 'promo_name',
      searchValue: (row) => `${shown(row.promo_name)} ${row.promo_name || ''}`,
      sortValue: (row) => shown(row.promo_name),
      render: (row) => (
        <Box component="span" sx={{ fontWeight: 600 }}>
          {shown(row.promo_name)}
        </Box>
      ),
    },
    {
      title: t('promo_code_web'),
      field: 'promo_code',
      nowrap: true,
      render: (row) => (row.promo_code ? <Box component="span" sx={{ fontWeight: 600, letterSpacing: '0.02em' }}>{row.promo_code}</Box> : null),
    },
    {
      title: t('description'),
      field: 'promo_description',
      minWidth: 180,
      searchValue: (row) => `${shown(row.promo_description)} ${row.promo_description || ''}`,
      sortValue: (row) => shown(row.promo_description),
      render: (row) => shown(row.promo_description),
    },
    { title: t('type'), field: 'promo_discount_type', lookup: { flat: t('flat'), percentage: t('percentage') } },
    { title: t('promo_discount_value'), field: 'promo_discount_value', type: 'number', searchValue: discountText, render: discountText },
    { title: t('max_limit'), field: 'max_promo_discount_value', type: 'money' },
    { title: t('min_limit'), field: 'min_order', type: 'money' },
    { title: t('end_date'), field: 'promo_validity', type: 'datetime' },
    { title: t('promo_usage'), field: 'promo_usage_limit', type: 'number' },
    {
      title: t('show_in_list'),
      field: 'promo_show',
      type: 'boolean',
      render: (row) =>
        isAdmin ? (
          <Switch
            checked={!!row.promo_show}
            onChange={() => toggleShow(row)}
            disabled={!critical}
            inputProps={{ 'aria-label': `${t('show_in_list')}: ${row.promo_code || shown(row.promo_name)}` }}
          />
        ) : (
          <YesNo value={row.promo_show} yes={t('yes')} no={t('no')} />
        ),
    },
    { title: t('promo_used_by'), field: 'user_avail', type: 'number' },
  ];

  const actions = [
    { key: 'edit', label: t('edit'), icon: <EditOutlinedIcon />, inline: true, onClick: goEdit },
    { key: 'delete', label: t('promos_delete', { defaultValue: 'Delete promo' }), icon: <DeleteOutlineRoundedIcon />, destructive: true, onClick: handleDelete },
  ];

  const title = calmCaps(t('promo_offer_title'));
  const addLabel = calmCaps(t('add_promo_title'));

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <PageHeader primaryAction={{ label: addLabel, icon: <AddRoundedIcon />, onClick: goAdd }} />
      <DataTable
        title={title}
        rows={rows}
        columns={columns}
        rowKey="id"
        loading={!!promodata.loading && !rows.length}
        refreshing={!!promodata.loading && rows.length > 0}
        countText={({ shown: n, total, query, loading }) =>
          loading
            ? t('loading', { defaultValue: 'Loading…' })
            : query
              ? t('table_count_shown', { shown: n, total, defaultValue: '{{shown}} of {{total}}' })
              : t('promos_count', { count: total, defaultValue_one: '{{count}} promo', defaultValue_other: '{{count}} promos', defaultValue: '{{count}} promos' })
        }
        rowLabel={(row) => row.promo_code || shown(row.promo_name)}
        actions={actions}
        searchPlaceholder={t('promos_search', { defaultValue: 'Search name, code or description' })}
        page={page}
        onPageChange={setPage}
        resortKey={mine}
        prefsKey="promos"
        stateKey="promos"
        minWidth={1280}
        emptyState={({ query }) =>
          query
            ? null
            : {
                icon: <LocalOfferOutlinedIcon />,
                title: t('promos_empty', { defaultValue: 'No promos yet' }),
                body: t('promos_empty_body', { defaultValue: 'Add a promo code to give customers a discount on their trips.' }),
                actionLabel: addLabel,
                onAction: goAdd,
              }
        }
      />
    </Box>
  );
}
